<template>
  <div class="resultados_tab">
    <!-- Header Section -->
    <div
      class="header_section_header is-flex is-flex-direction-row is-justify-content-space-between is-align-items-end mb-5 sticky-header">
      <div>
        <div class="header_title">Estado de resultados</div>
        <div class="header_description mb-4">
          Visualiza el estado de tu negocio en un solo lugar.
        </div>

        <!-- Sub-Navigation Filters -->
        <div class="sub_nav_filters">
          <button v-for="period in filterOptions" :key="period.id" class="filter_btn"
            :class="{ 'is-active': filterPeriod === period.id }" @click="updateFilterPeriod(period.id)">
            {{ period.label }}
          </button>
        </div>
      </div>

      <!-- Right Actions -->
      <div class="right_actions is-flex">
        <div class="calendar_picker is-flex is-align-items-center mr-3">
          <AnualCalendar class="calendar" :value="endMonth" @onDateChange="updateEndMonth" :disableNewerDates="true"
            :disabledFrom="getNow" formatLabelDate="MMMM YYYY" :alignRight="true" />
        </div>
        <button class="theme_btn_white is-flex is-align-items-center">
          <DownloadFile class="mr-2" style="width: 16px; height: 16px;" />
          Exportar
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="underHeader">
      <!-- <div class="is-flex is-full-width has-gap-cards">
        <CardResults class="is-flex-grow-1" tittleCard="Ventas"
          :mainAmount="tableRowHeaders['ventas_netas']?.itemsDict[getHeadersTable[getHeadersTable.length - 1]] ?? 0"
          :mainDetail="allDetails[0]" tittleSection="Métricas" :sectionDetails="allSectionDetails[0]" />
        <CardResults class="is-flex-grow-1" tittleCard="Costos"
          :mainAmount="tableRowHeaders['costos_directos']?.itemsDict[getHeadersTable[getHeadersTable.length - 1]] ?? 0"
          :mainDetail="allDetails[1]" tittleSection="Costos más altos" :sectionDetails="allSectionDetails[1]" />
        <CardResults class="is-flex-grow-1" tittleCard="Gastos"
          :mainAmount="tableRowHeaders['gastos_totales']?.itemsDict[getHeadersTable[getHeadersTable.length - 1]] ?? 0"
          :mainDetail="allDetails[2]" tittleSection="Gastos más altos" :sectionDetails="allSectionDetails[2]" />
        <CardResults class="is-flex-grow-1" tittleCard="EBITDA"
          :mainAmount="tableRowHeaders['EBITDA']?.itemsDict[getHeadersTable[getHeadersTable.length - 1]] ?? 0"
          :mainDetail="allDetails[3]" tittleSection="Utilidades" :sectionDetails="allSectionDetails[3]" />
      </div> -->

      <!-- Table Section -->
      <div class="mt-6 p-5 container_section">
        <div class="table_header is-flex is-justify-content-space-between is-align-items-center mb-4">
          <div class="is-flex is-align-items-center">
            <span class="tittle_section">Estado de Resultados</span>
          </div>
          <div class="table_actions is-flex">
            <button class="action_btn mr-2">
              <ChartLine class="mr-1" style="width: 16px; height: 16px;" />
              Ver Gráfico
            </button>
            <div v-if="!['ytd', 'year'].includes(filterPeriod)" style="min-width: 165px">
              <SelectComponent :value="selectedPeriodRange" @input="updateSelectedPeriodRange" theme="report_theme"
                :options="periodOptions" />
            </div>
          </div>
        </div>

        <section>
          <div class="table_container" ref="mainTable" v-if="!isLoading">
            <table class="custom_table">
              <colgroup>
                <col style="width: 200px;">
                <col style="width: 160px;" v-for="header in getHeadersTable" :key="header">
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" class="sticky-column header_label nobreak"></th>
                  <th scope="col" class="has-text-right nobreak is-capitalized" v-for="header in getHeadersTable"
                    :key="header">
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in visibleRows" :key="row.id">
                  <th scope="row" class="sticky-column nobreak" :class="row.theme ?? 'theme_row_white'">
                    <span>{{ row.label }}</span>
                    <span v-if="row.id === 'gastos_totales'" class="is-pulled-right actual_button_arrow_down"
                      @click="changeViewTotalBills()">
                      <ArrowDown class="color_arrow" :class="{ rotate_arrow: totalBillsExpanded }" />
                    </span>
                  </th>
                  <td v-for="header in getHeadersTable" :key="header" class="has-text-right nobreak"
                    :class="row.theme ?? 'theme_row_white'">
                    <template v-if="row.isMoney">
                      {{ money(row.itemsDict[header] ?? 0) }}
                    </template>
                    <template v-else>
                      {{ Math.round(row.itemsDict[header] ?? 0) }}%
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="loading_state py-6 has-text-centered">
            <p>Cargando datos...</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { parseToMoney } from "@/utils/currency";
import { PERIOD_TYPES, REPORT_DETAILS_CONFIG } from "../config/reportStructure";

// import CardResults from "@/components/financialReports/CardResults.vue";
import SelectComponent from "@/components/classification/SelectComponent";
import AnualCalendar from "@/components/classification/AnualCalendar";
import ArrowDown from "@/components/svg/ArrowDown.vue";
import ChartLine from "@/components/svg/ChartLine.vue";
import DownloadFile from "@/components/svg/DownloadFile.vue";

export default {
  name: "ResultadosTab",
  components: {
    // CardResults,
    SelectComponent,
    AnualCalendar,
    ArrowDown,
    ChartLine,
    DownloadFile
  },
  data() {
    return {
      totalBillsExpanded: false,
      periodOptions: PERIOD_TYPES,
      filterOptions: [
        { id: 'month', label: 'Mensual' },
        { id: 'quarter', label: 'Trimestral' },
        { id: 'ytd', label: 'YTD' },
        { id: 'year', label: 'Anual' }
      ]
    };
  },
  computed: {
    ...mapState('reportsFilters', ['endMonth', 'selectedPeriodRange']),
    ...mapState('financialReports', [
      'filterPeriod',
      'tableRowHeaders',
      'netSellsDetail',
      'maxCostsDetail',
      'maxExpensesDetail',
      'isLoading'
    ]),
    ...mapGetters('financialReports', {
      getHeadersTableFunc: 'getHeadersTable'
    }),
    endMonthProxy: {
      get() { return this.endMonth; },
      set(val) { this.updateEndMonth(val); }
    },
    getHeadersTable() {
      return this.getHeadersTableFunc();
    },
    visibleRows() {
      const headers = this.tableRowHeaders;
      return Object.entries(headers)
        .filter(([, row]) => row.isShowed !== false)
        .map(([key, row]) => ({ ...row, id: key }));
    },
    getNow() {
      return moment().toDate();
    },
    allDetails() {
      let ventasLabel = '';
      switch (this.filterPeriod) {
        case 'month':
          ventasLabel = 'MoM';
          break;
        case 'quarter':
          ventasLabel = 'QoQ';
          break;
        case 'ytd':
        case 'year':
          ventasLabel = 'YoY';
          break;
      }

      const headers = this.getHeadersTable;
      if (headers.length === 0) return [];

      const lastHeader = headers[headers.length - 1];
      const prevHeader = headers.length > 1 ? headers[headers.length - 2] : null;

      return REPORT_DETAILS_CONFIG.map(det => {
        let newValue = Math.round(
          this.tableRowHeaders[det.key]?.itemsDict?.[lastHeader] ?? 0
        ) + "%";

        if (det.key === 'ventas_netas') {
          const numerator = Number(this.tableRowHeaders[det.key]?.itemsDict?.[lastHeader] ?? 0);
          const denominator = prevHeader ? Number(this.tableRowHeaders[det.key]?.itemsDict?.[prevHeader] ?? 0) : 0;
          if (denominator !== 0) {
            newValue = Math.round((numerator / denominator - 1) * 100) + '% ' + ventasLabel;
          } else {
            newValue = '0% ' + ventasLabel;
          }
        }

        return {
          ...det,
          value: newValue
        };
      });
    },
    allSectionDetails() {
      const lastHeader = this.getHeadersTable[this.getHeadersTable.length - 1];
      return [
        [
          { label: 'Ticket Promedio', value: this.money(this.netSellsDetail?.ticket_average ?? 0) },
          { label: 'Cantidad de Clientes', value: this.netSellsDetail?.nro_counterparts ?? '0' }
        ],
        [
          { label: 'Cuenta', value: this.maxCostsDetail?.max_cost_account ?? '_' },
          { label: 'Contraparte', value: this.maxCostsDetail?.max_cost_counterpart ?? '_' }
        ],
        [
          { label: 'Cuenta', value: this.maxExpensesDetail?.max_expenses_account ?? '_' },
          { label: 'Contraparte', value: this.maxExpensesDetail?.max_expenses_counterpart ?? '_' }
        ],
        [
          { label: 'Antes de Impuestos', value: this.money(this.tableRowHeaders['utilidad_antes_de_impuestos']?.itemsDict[lastHeader] ?? 0) },
          { label: 'Margen Neto %', value: Math.round(this.tableRowHeaders['margen_neto_%']?.itemsDict[lastHeader] ?? 0) + '%' }
        ]
      ];
    },
    // Firma reactiva consolidada — un solo trigger para fetch aunque
    // varios filtros cambien en la misma transacción.
    fetchSignature() {
      return [this.filterPeriod, this.endMonth, this.selectedPeriodRange].join("|");
    },
  },
  mounted() {
    this.fetchReportData();
  },
  methods: {
    ...mapActions('financialReports', ['fetchReportData']),
    ...mapActions('reportsFilters', ['setPeriodRange']),
    ...mapMutations('reportsFilters', ['SET_END_MONTH']),
    ...mapMutations('financialReports', [
      'SET_FILTER_PERIOD',
      'TOGGLE_CHILD_ROWS'
    ]),
    updateFilterPeriod(period) {
      this.SET_FILTER_PERIOD(period);
    },
    updateEndMonth(date) {
      this.SET_END_MONTH(date);
    },
    updateSelectedPeriodRange(range) {
      this.setPeriodRange(range);
    },
    changeViewTotalBills() {
      this.totalBillsExpanded = !this.totalBillsExpanded;
      this.TOGGLE_CHILD_ROWS(this.totalBillsExpanded);
    },
    money(value) {
      return parseToMoney(value);
    },
    scrollToRight() {
      this.$nextTick(() => {
        const table = this.$refs.mainTable;
        if (table) {
          table.scrollLeft = table.scrollWidth;
        }
      });
    }
  },
  watch: {
    tableRowHeaders() {
      this.scrollToRight();
    },
    fetchSignature() {
      this.fetchReportData();
    },
    isLoading(val) {
      if (!val) {
        this.scrollToRight();
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.resultados_tab {
  position: relative;
  padding-bottom: 40px;
  overflow-y: auto;
  height: 100%;
}

.header_title {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
}

.sticky-header {
  // position: sticky;
  // top: 0;
  // z-index: 20;
  background-color: #ffffff;
  padding-bottom: 8px;
}


.sub_nav_filters {
  display: flex;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  width: fit-content;
}

.filter_btn {
  padding: 6px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &.is-active {
    background: #0d3c3f;
    color: #ffffff;
  }
}

.calendar_picker {
  background: #0d3c3f;
  color: white;
  border-radius: 8px;
  font-weight: 500;

  ::v-deep .calendar-label {
    color: white !important;
  }
}

.theme_btn_white {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 500;
  color: #374151;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
}

.action_btn {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
}

.tittle_section {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.container_section {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.table_container {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  max-width: 100%;
}

.sticky-column {
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: inherit;
  border-right: 1px solid #e5e7eb;
}

.custom_table {
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  td,
  th {
    height: 44px;
    padding: 12px 16px !important;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
  }

  thead th,
  td {
    text-align: end;
  }

  thead th {
    padding: 12px 24px;
    color: #6b7280;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    background-color: #f9fafb;
  }

  tbody th {
    color: #111827;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
  }

  tbody td {
    color: #4b5563;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  .theme_row_white {
    background-color: white;
  }

  .theme_row_blue {
    background-color: #eff6ff;
  }

  .theme_row_azure {
    background-color: #ecfeff;
  }

  .theme_row_yellow {
    background-color: #fefce8;
  }

  .theme_row_purple {
    background-color: #f5f3ff;
  }

  .theme_row_orange {
    background-color: #fffaf0;
  }

  .theme_row_detail_bills {
    padding-left: 3em !important;
    background: #fffdf2;
  }
}

.actual_button_arrow_down {
  width: 18px;
  height: 18px;
  vertical-align: middle;
}

.color_arrow {
  filter: brightness(0) saturate(100%) invert(20%) sepia(20%) saturate(500%) hue-rotate(180deg);
}

.rotate_arrow {
  transform: rotate(180deg);
}

.has-gap-cards {
  gap: 16px;
}
</style>
